import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Box,
  Stack,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import axiosInstance from "../../../axios/axiosInstance";
import { certificationOptions } from "../../corporate/Constants";
import { certificationToFormValues } from "../../../utils/profileFieldNormalization";

export default function CertificationDialog({
  open,
  onClose,
  onSave,
  formData,
  setFormData,
  editingId,
  certificationList,
}) {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: formData,
  });

  const noExpiry = watch("noExpiry");
  const selectedProvider = watch("issuingOrganization");
  const declarationAccepted = watch("declaration");

  const [orgOptions, setOrgOptions] = useState([]);
  const predefinedCertNames = certificationOptions.map((opt) => opt.label);
  const [certOptions, setCertOptions] = useState(predefinedCertNames);
  const [orgLoading, setOrgLoading] = useState(false);
  const [certLoading, setCertLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  useEffect(() => {
    if (formData) reset(certificationToFormValues(formData));
  }, [formData, reset]);

  // --- Debounce helper ---
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  // --- Fetch organizations ---
  const fetchOrganizations = async (query) => {
    if (!query) {
      setOrgOptions([]);
      return;
    }

    setOrgLoading(true);
    try {
      const res = await axiosInstance.get(
        `api/profile/certification-providers?name=${encodeURIComponent(query)}`,
      );
      setOrgOptions(res.data || []);
    } catch (err) {
      console.error("Error fetching organizations:", err);
    } finally {
      setOrgLoading(false);
    }
  };

  const debouncedFetchOrgs = useCallback(debounce(fetchOrganizations, 400), []);

  // --- Fetch certifications ---
  const fetchCertifications = async (providerId, query) => {
    if (!providerId && !query) {
      // When no provider and no query, show the predefined certification list
      setCertOptions(predefinedCertNames);
      return;
    }

    setCertLoading(true);
    try {
      const url = `api/profile/certifications?provider_id=${
        providerId || ""
      }&name=${encodeURIComponent(query || "")}`;
      const res = await axiosInstance.get(url);
      const apiCerts = res.data || [];
      const apiCertNames = apiCerts.map((cert) => cert.name);

      // Merge predefined options with API results, ensuring no duplicates
      setCertOptions(
        Array.from(new Set([...predefinedCertNames, ...apiCertNames])),
      );
    } catch (err) {
      console.error("Error fetching certifications:", err);
    } finally {
      setCertLoading(false);
    }
  };

  const debouncedFetchCerts = useCallback(
    debounce(fetchCertifications, 400),
    [],
  );

  const handleFormSubmit = (data) => {
    // Check for duplicates
    if (certificationList?.length > 0) {
      const isDuplicate = certificationList.some(
        (cert) =>
          cert.id !== editingId &&
          cert.certificationName === data.certificationName &&
          cert.issuingOrganization === data.issuingOrganization,
      );

      if (isDuplicate) {
        setError("certificationName", {
          type: "manual",
          message: "This certification already exists.",
        });
        return;
      }
    }

    const { declaration, ...restData } = data;
    const transformedData = {
      ...restData,
      terms_accepted: declaration ? 1 : 0,
    };
    onSave(transformedData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "#6c5ce7",
          color: "white",
        }}
      >
        {editingId ? "Edit Certification" : "Add Certification"}
      </DialogTitle>

      <DialogContent
        sx={{
          overflowY: "auto",
          maxHeight: "80vh",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 8,
          },
        }}
      >
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Issuing Organization (first) */}

            {/* Certification Name (second) */}
            <Controller
              name="certificationName"
              control={control}
              rules={{ required: "Certification name is required" }}
              render={({ field }) => (
                <Autocomplete
                  freeSolo
                  options={certOptions}
                  loading={certLoading}
                  value={field.value || ""}
                  onInputChange={(e, value) => {
                    field.onChange(value);
                    debouncedFetchCerts(selectedOrgId, value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Certification Name *"
                      fullWidth
                      error={!!errors.certificationName}
                      helperText={errors.certificationName?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {certLoading ? (
                              <CircularProgress size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="issuingOrganization"
              control={control}
              rules={{ required: "Issuing organization is required" }}
              render={({ field }) => (
                <Autocomplete
                  freeSolo
                  options={orgOptions.map((org) => org.name)}
                  loading={orgLoading}
                  value={field.value || ""}
                  onInputChange={(e, value) => {
                    field.onChange(value);
                    debouncedFetchOrgs(value);
                  }}
                  onChange={(e, newValue) => {
                    field.onChange(newValue);
                    const selectedOrg = orgOptions.find(
                      (org) => org.name === newValue,
                    );
                    setSelectedOrgId(selectedOrg ? selectedOrg.id : null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Issuing Organization *"
                      fullWidth
                      error={!!errors.issuingOrganization}
                      helperText={errors.issuingOrganization?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {orgLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />

            {/* Issue & Expiry Dates */}
            {/* <Box sx={{ display: "flex", gap: 2 }}>
              <Controller
                name="issueDate"
                control={control}
                rules={{
                  required: "Issue date is required",
                  validate: (value) => {
                    if (!/^(0[1-9]|1[0-2])\/\d{4}$/.test(value)) {
                      return "Use MM/YYYY format";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Issue Date *"
                    placeholder="MM/YYYY"
                    fullWidth
                    error={!!errors.issueDate}
                    helperText={errors.issueDate?.message}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^\d]/g, "");
                      if (value.length >= 3)
                        value = value.slice(0, 2) + "/" + value.slice(2, 6);
                      if (value.length > 7) value = value.slice(0, 7);
                      field.onChange(value);
                    }}
                  />
                )}
              />

              <Controller
                name="expiryDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (noExpiry) return true;
                    if (!value) return "Expiry date is required";
                    if (!/^(0[1-9]|1[0-2])\/\d{4}$/.test(value)) {
                      return "Use MM/YYYY format";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Expiry Date"
                    placeholder="MM/YYYY"
                    fullWidth
                    disabled={noExpiry}
                    error={!!errors.expiryDate}
                    helperText={errors.expiryDate?.message}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^\d]/g, "");
                      if (value.length >= 3)
                        value = value.slice(0, 2) + "/" + value.slice(2, 6);
                      if (value.length > 7) value = value.slice(0, 7);
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </Box> */}

            <Box sx={{ display: "flex", gap: 2 }}>
              <Controller
                name="issueDate"
                control={control}
                rules={{
                  required: "Issue date is required",
                  validate: (value) => {
                    if (!/^(0[1-9]|1[0-2])\/\d{4}$/.test(value)) {
                      return "Use MM/YYYY format";
                    }
                    const [month, year] = value.split("/").map(Number);
                    const now = new Date();
                    const currentYear = now.getFullYear();
                    const currentMonth = now.getMonth() + 1;

                    if (
                      year > currentYear ||
                      (year === currentYear && month > currentMonth)
                    ) {
                      return "Issue date cannot be in the future";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Issue Date *"
                    placeholder="MM/YYYY"
                    fullWidth
                    error={!!errors.issueDate}
                    helperText={errors.issueDate?.message}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^\d]/g, "");
                      if (value.length >= 3)
                        value = value.slice(0, 2) + "/" + value.slice(2, 6);
                      if (value.length > 7) value = value.slice(0, 7);
                      field.onChange(value);
                    }}
                  />
                )}
              />

              <Controller
                name="expiryDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (noExpiry) return true;
                    if (!value) return "Expiry date is required";
                    if (!/^(0[1-9]|1[0-2])\/\d{4}$/.test(value)) {
                      return "Use MM/YYYY format";
                    }

                    const issueValue = getValues("issueDate");
                    if (
                      issueValue &&
                      /^(0[1-9]|1[0-2])\/\d{4}$/.test(issueValue)
                    ) {
                      const [issueMonth, issueYear] = issueValue
                        .split("/")
                        .map(Number);
                      const [expiryMonth, expiryYear] = value
                        .split("/")
                        .map(Number);
                      const issueDate = new Date(issueYear, issueMonth - 1);
                      const expiryDate = new Date(expiryYear, expiryMonth - 1);

                      if (expiryDate < issueDate) {
                        return "Expiry date cannot be earlier than issue date";
                      }
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Expiry Date"
                    placeholder="MM/YYYY"
                    fullWidth
                    disabled={noExpiry}
                    error={!!errors.expiryDate}
                    helperText={errors.expiryDate?.message}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^\d]/g, "");
                      if (value.length >= 3)
                        value = value.slice(0, 2) + "/" + value.slice(2, 6);
                      if (value.length > 7) value = value.slice(0, 7);
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </Box>

            {/* No Expiry */}
            <Controller
              name="noExpiry"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="No Expiry"
                />
              )}
            />

            {/* Credential ID */}
            <Controller
              name="credentialId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Credential ID (Optional)"
                  fullWidth
                />
              )}
            />

            {/* Credential URL */}
            <Controller
              name="credentialUrl"
              control={control}
              rules={{
                pattern: {
                  value:
                    /^(https?:\/\/)?([\w\-])+\.{1}([a-zA-Z]{2,63})([\/\w\-.]*)*\/?$/,
                  message: "Enter a valid URL",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Credential URL (Optional)"
                  fullWidth
                  placeholder="https://"
                  error={!!errors.credentialUrl}
                  helperText={errors.credentialUrl?.message}
                />
              )}
            />

            {/* Declaration Checkbox */}
            <Controller
              name="declaration"
              control={control}
              rules={{
                required: "You must accept the declaration to proceed",
              }}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I hereby declare that the information I have submitted is
                      true, correct, and complete. I acknowledge that EQuAlly
                      reserves the right to verify my qualifications and request
                      official documents and/or certificates at any stage of the
                      recruitment process.
                    </Typography>
                  }
                />
              )}
            />
            {errors.declaration && (
              <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                {errors.declaration.message}
              </Typography>
            )}
          </Stack>

          <DialogActions sx={{ mt: 2 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!declarationAccepted}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}
